import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, Subscription, interval, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, map, take, first, tap } from 'rxjs/operators';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { Router } from "@angular/router";

import { User } from 'app/core/user/user.types';

import { auth } from 'firebase/firebase-auth';
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/compat/firestore';
import { AngularFireDatabase, AngularFireList } from '@angular/fire/compat/database';

import { OverviewService } from 'app/modules/pages/trailer-systems/overview/system-overview.service';


@Injectable()
export class AuthService
{
    sidenavMenu: BehaviorSubject<any>;

    userState: any;

    user$: Observable<any>;

    showLogin: boolean = true

    // Subscriptions
    waitPage_timer: Subscription;
    userSubs: Subscription;

    userData = {}

    organizationPrimaryLogo: string = "assets/images/logo/25-FTK-19387 - PWRTEK Logo FINAL.png"
    organizationSecondaryLogo: string = "assets/images/logo/25-FTK-19387 - PWRTEK Logo FINAL.png"


    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient, private _userService: UserService, public firestore: AngularFirestore,
        public afAuth: AngularFireAuth, public router: Router, public ngZone: NgZone, private db: AngularFireDatabase,
        private overviewService: OverviewService)
    {
        this.sidenavMenu = new BehaviorSubject<any>([]);

        //// Get auth data, then get firestore user document || null
        this.user$ = this.afAuth.authState.pipe(switchMap(user => {
            if (user) {
                //console.log(user)
                //console.log( user.uid)
                this.userState = user;

                if(!this.userSubs){
                    // Set subscription for user status
                    this.userSubs = this.updateOnConnect(user.uid).subscribe();
                    document.addEventListener("visibilitychange", () => this.updateOnAway())
                }

                this.db.object(`user_status/${user.uid}`).query.ref.onDisconnect()
                    .update({
                    status: 'offline',
                    timestamp: this.timestamp
                });

                return this.firestore.collection('users').doc(user.uid).get()

            } else {
                sessionStorage.setItem('user', null);

                return of(null)
            }
        }))

        this.user$.subscribe(res => {
            if(res){
                // console.log(res)
                // The user has been successfully authorized
                this.showLogin = false

                let user = res.data();
                //console.log(user);

                if(!user){

                    if (Object.keys(this.userData).length !== 0){
                        user = this.userData
                    }

                    const user_ = JSON.parse(sessionStorage.getItem('user'));
                    if (Object.keys(user_).length !== 0){
                        user = user_
                    }
                }

                // Make sure user status is initially set to online
                if(user.hasOwnProperty("status")){
                    user["status"] = "online"
                }

                // Store the user on the user service
                this._userService.user = user;


                let loading = function(user, waitPage_timer, router){

                    // Check if temporal user data is present
                    const user_ = JSON.parse(sessionStorage.getItem('user'));
                    //console.log(user_)
                    if(!user_){

                        let userProfile = {}

                        userProfile["id"] = user.id
                        userProfile["displayName"] = user.firstName + " " + user.lastName
                        userProfile["email"] = user.email
                        userProfile["emailVerified"] = user.emailVerified
                        userProfile["emailAlerts"] = user.emailAlerts
                        userProfile["phoneNumber"] = user.phoneNumber
                        userProfile["textAlerts"] = user.textAlerts

                        userProfile["role"] = user.role
                        userProfile["organization"] = user.organization

                        userProfile["avatar"] = user.avatar
                        userProfile["rememberme"] = user.rememberme

                        userProfile["lastLoginAt"] = user.lastLoginAt

                        userProfile["timestamp"] = user.timestamp

                        // Store temporal user data in session for later use
                        sessionStorage.setItem('user', JSON.stringify(userProfile));

                        if (waitPage_timer) {waitPage_timer.unsubscribe();}
                        waitPage_timer = interval(1500).subscribe(
                            (val) => { router.navigate(['/trailer-systems/list']); waitPage_timer.unsubscribe();
                        });
                    }
                }

                //console.log(user)

                const permissionRef = this.firestore.firestore.collection('permissions').doc(user.organization)

                permissionRef.get().then(snapshot => {

                    let permissionData = snapshot.data()

                    //console.log(permissionData)

                    if(permissionData.hasOwnProperty(user.role)){

                        this.sidenavMenu.next(permissionData[user.role]["menu"])

                        loading(user, this.waitPage_timer, this.router)

                    }else{

                        this.sidenavMenu.next(permissionData["thirdparty"]["menu"])

                        loading(user, this.waitPage_timer, this.router)
                    }
                }).catch(err => {
                    console.log("Error getting permissions", err);
                })
            }
        })
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        return new Observable(observer => {
            this.afAuth.sendPasswordResetEmail(email).then(() => {
                observer.next(true);
                observer.complete();
            }).catch(error => {
                observer.error(error);
            });
        });
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
     signIn(credentials: { email: string; password: string }) {
        //console.log(credentials)

        return this.afAuth.setPersistence(credentials["rememberMe"] === true ? 'local' : 'session').then(_ => {
            return this.afAuth.signInWithEmailAndPassword(credentials.email, credentials.password).then((result) => {

                this.SetUserData(result.user, credentials)

            }).catch((error) => {
                console.log("Error logging in")
                throw error
            })
        })
    }


    /**
     * Sign out
     */
    signOut(): Observable<any>{
        //console.log("login out")
        this.overviewService.removeSystemUser()

        this.setPresence("offline").then(data=>{

            if(this.userSubs) this.userSubs.unsubscribe(); this.userSubs = null;

            document.removeEventListener("visibilitychange", () => this.updateOnAway())

            this.afAuth.signOut().then(() => {
                this.showLogin = true;
                this.userState = null;
            })
        })


        // Remove the access token from the local storage
        sessionStorage.removeItem('user');

        // Return the observable
        return of(true);
    }


    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        const user = JSON.parse(sessionStorage.getItem('user'));
        //console.log(user)
        return of ((user !== null) ? true : false);
    }

    SetUserData(user, credentials) {
        //console.log(user)
        let userState: User
        userState = {
            id: user.uid,
            email: user.email,
            phoneNumber: user.phoneNumber,
            textAlerts: user.textAlerts,
            emailAlerts: user.emailAlerts,
            allowCmd: user.allowCmd,
            avatar: user.photoURL,
            emailVerified: user.emailVerified,
            lastLoginAt : user.metadata.lastSignInTime,
            //lastLoginAt_epoch : user.metadata.b
        }

        if(credentials["rememberMe"] === true){ //local
            let userState2 = {
                id: user.uid,
                email: user.email,
                rememberMe: credentials["rememberMe"]

            }
            localStorage.setItem('pf_trailers', JSON.stringify(userState2));

        }else{ //session
            let userProfile = localStorage.getItem('pf_trailers');
            if(userProfile){
                localStorage.removeItem('pf_trailers');
            }
        }

        let userParams = function(user){
            if (!user.hasOwnProperty("role")){
                userState["role"] = "user"
            }else{
                userState["role"] = user.role
            }

            if (!user.hasOwnProperty("organization")){
                userState["organization"] = ""
            }else{
                userState["organization"] = user.organization
            }

            if (!user.hasOwnProperty("status")){
                userState["status"] = "online"
            }else{
                userState["status"] = "online"
            }

            if (!userState["avatar"]){
                userState["avatar"] = 'assets/images/avatars/default.jpg'
            }

            if (!user.hasOwnProperty("firstName")){
                userState["firstName"] = ''
            }

            if (!user.hasOwnProperty("lastName")){
                userState["lastName"] = ''
            }

            if (!user.hasOwnProperty("textAlerts")){
                userState["textAlerts"] = false
            }else{
                userState["textAlerts"] = user.textAlerts
            }

            if (!user.hasOwnProperty("emailAlerts")){
                userState["emailAlerts"] = false
            }else{
                userState["emailAlerts"] = user.emailAlerts
            }

            if (!user.hasOwnProperty("allowCmd")){
                userState["allowCmd"] = false
            }else{
                userState["allowCmd"] = user.allowCmd
            }

            if (!user.hasOwnProperty("phoneNumber")){
                userState["phoneNumber"] = null
            }else{
                userState["phoneNumber"] = user.phoneNumber
            }
        }

       const userRef = this.firestore.firestore.collection('users').doc(user.uid)

       userRef.get().then(snapshot => {

            let user2 = snapshot.data()

            if (user2){
                //console.log(user2)
                userParams(user2)
            }/*else{
                //console.log(user)
                userParams(user)
            }*/

            //console.log(userState)
            //console.log(user2)

            this.userData = userState

            /*userRef.set(userState, {
                merge: true
            })*/
            userRef.update(userState)

            const organizationRef = this.firestore.firestore.collection('organizations').where('name', '==', userState["organization"])

            organizationRef.get().then(snapshot => {
                //console.log(snapshot)
                snapshot.forEach(doc => {
                    let organization = doc.data()
                    this.organizationPrimaryLogo = organization["primaryLogo"]
                    this.organizationSecondaryLogo = organization["secondaryLogo"]
                })

            }).catch(err => {
                console.log("Error getting sub-collection documents", err);
            })
        }).catch(err => {
            console.log("Error getting sub-collection documents", err);
        })
    }

    updateOnConnect(uid){
        return this.db.object('.info/connected').valueChanges().pipe(
            map(connected => {
                //console.log(connected)
                let status = connected ? 'online' : 'offline'
                this.setPresence(status)
            })
        );
    }

    updateOnAway() {
        if (document.visibilityState === 'hidden') {
        this.setPresence('away');
        } else {
        this.setPresence('online');
        }
    }

    getUser() {
        return this.afAuth.authState.pipe(first()).toPromise();
    }

    async setPresence(status: string) {
        const user = await this.getUser();
        //console.log(user)
        if (user) {
            this.db.object(`user_status/${user.uid}`).update({
                status: status,
                timestamp: this.timestamp
            });
            //console.log(user.uid)
            /*return await this.firestore.firestore.collection('users').doc(user.uid).set({ status: status, timestamp: this.timestamp }, {
                merge: true
            });*/
            return await this.firestore.firestore.collection('users').doc(user.uid).update({ status: status, timestamp: this.timestamp });
        }
    }

    get timestamp() {
        return Date.now();
    }
}
